import type { AudioRecordingObservable } from "@/store/audio-recordings";
import { findLatestAppEvent } from "@/store/audio-recordings/app-events";
import {
  AudioRecordingAppEventKind,
  AudioRecordingProcessingStatus,
  AudioRecordingRecordingStatus,
  AudioRecordingRetranscriptionStatus,
  AudioRecordingUploadStatus,
} from "@/store/audio-recordings/types";
import {
  AUDIO_ARTIFACT_MISSING_MESSAGE,
  NO_USABLE_AUDIO_FOUND_MESSAGE,
} from "@/modules/audio-recording/final-audio-validation";
import { AudioCapturePhase } from "@/store/audio/pipeline/audio-capture.types";

import { PipelineSnapshot, Stage, StageStatus, TerminalOutcome } from "./audio-pipeline.types";

export type CapturePipelineUiRetryTargets = {
  recordAgain: boolean;
  upload: boolean;
  retranscribe: boolean;
  transform: boolean;
  retryAll: boolean;
};

export type CapturePipelineUiState = {
  phase: AudioCapturePhase;
  isProcessing: boolean;
  isFinished: boolean;
  failureMessage: string | null;
  terminalOutcome: TerminalOutcome | null;
  retryTargets: CapturePipelineUiRetryTargets;
  processingLabel: string;
  /** True when the current pipeline stage should surface an "error" color on
   * the voice indicator chip (retranscribe/transform failures). Background
   * upload failures on the happy path do not flip this. */
  hasPipelineError: boolean;
};

type ProjectionInput = {
  snapshot: PipelineSnapshot | null;
  recording: AudioRecordingObservable | null;
  isInitializing: boolean;
  isRecordingPaused: boolean;
};

const EMPTY_RETRY_TARGETS: CapturePipelineUiRetryTargets = {
  recordAgain: false,
  upload: false,
  retranscribe: false,
  transform: false,
  retryAll: false,
};

type TerminalUiState = {
  terminalOutcome: TerminalOutcome | null;
  failureMessage: string | null;
};

/**
 * Projects a pipeline snapshot + live recording state into the UI shape
 * consumed by the audio capture surface. This is the only function that
 * translates canonical pipeline state into `AudioCapturePhase` — every UI
 * component should read through here rather than inferring from scattered
 * store booleans.
 */
export function projectCapturePipelineUiState({
  snapshot,
  recording,
  isInitializing,
  isRecordingPaused,
}: ProjectionInput): CapturePipelineUiState {
  const phase = derivePhase({ snapshot, recording, isInitializing, isRecordingPaused });
  const { terminalOutcome, failureMessage } = resolveTerminalUiState({ snapshot, recording });
  const retryTargets = deriveRetryTargets(terminalOutcome);
  const hasPipelineError = terminalOutcome !== null && terminalOutcome !== TerminalOutcome.Success;

  const isProcessing =
    phase === AudioCapturePhase.Processing || phase === AudioCapturePhase.Uploading;
  const isFinished = isRecordingInactivePhase(phase);

  return {
    phase,
    isProcessing,
    isFinished,
    failureMessage,
    terminalOutcome,
    retryTargets,
    processingLabel: deriveProcessingLabel({ phase, snapshot }),
    hasPipelineError,
  };
}

function derivePhase({
  snapshot,
  recording,
  isInitializing,
  isRecordingPaused,
}: ProjectionInput): AudioCapturePhase {
  if (isInitializing) return AudioCapturePhase.Initializing;
  if (!recording || !recording.hasStartedRecording) return AudioCapturePhase.Ready;
  if (recording.isRecording) {
    return isRecordingPaused ? AudioCapturePhase.Paused : AudioCapturePhase.Recording;
  }

  // Recording has stopped. Consult the pipeline snapshot.
  if (!snapshot) {
    return AudioCapturePhase.Stopped;
  }

  if (snapshot.stage === Stage.Done) {
    return AudioCapturePhase.Stopped;
  }

  if (snapshot.stage === Stage.UploadSource || snapshot.stage === Stage.RetranscribeFromSource) {
    return AudioCapturePhase.Uploading;
  }

  // Finalize, ValidateAudio, ResolveTranscriptSource, Transform all show as
  // generic "Processing" from the user's perspective.
  if (isPreProcessingStage(snapshot.stage, snapshot.stageStatus)) {
    return AudioCapturePhase.Processing;
  }

  return AudioCapturePhase.Stopped;
}

function isPreProcessingStage(stage: Stage, status: StageStatus): boolean {
  if (status === "done" && stage === Stage.Transform) return false;
  return (
    stage === Stage.Finalize ||
    stage === Stage.ValidateAudio ||
    stage === Stage.ResolveTranscriptSource ||
    stage === Stage.Transform
  );
}

function isRecordingInactivePhase(phase: AudioCapturePhase): boolean {
  return (
    phase === AudioCapturePhase.Stopped ||
    phase === AudioCapturePhase.Uploading ||
    phase === AudioCapturePhase.Processing
  );
}

function deriveRetryTargets(outcome: TerminalOutcome | null): CapturePipelineUiRetryTargets {
  if (outcome === null) return EMPTY_RETRY_TARGETS;
  switch (outcome) {
    case TerminalOutcome.NoUsableAudioFound:
    case TerminalOutcome.AudioArtifactMissing:
      return {
        recordAgain: true,
        upload: false,
        retranscribe: false,
        transform: false,
        retryAll: false,
      };
    case TerminalOutcome.UploadFailed:
      return {
        recordAgain: false,
        upload: true,
        retranscribe: false,
        transform: false,
        retryAll: false,
      };
    case TerminalOutcome.RetranscribeFailed:
      return {
        recordAgain: false,
        upload: false,
        retranscribe: true,
        transform: false,
        retryAll: false,
      };
    case TerminalOutcome.TransformFailed:
      return {
        recordAgain: false,
        upload: false,
        retranscribe: false,
        transform: true,
        retryAll: false,
      };
    case TerminalOutcome.UnexpectedFailed:
      return {
        recordAgain: false,
        upload: false,
        retranscribe: false,
        transform: false,
        retryAll: true,
      };
    case TerminalOutcome.Success:
      return EMPTY_RETRY_TARGETS;
  }
}

function resolveTerminalUiState({
  snapshot,
  recording,
}: {
  snapshot: PipelineSnapshot | null;
  recording: AudioRecordingObservable | null;
}): TerminalUiState {
  if (snapshot?.stage === Stage.Done) {
    return {
      terminalOutcome: snapshot.terminalOutcome,
      failureMessage:
        snapshot.terminalOutcome !== null && snapshot.terminalOutcome !== TerminalOutcome.Success
          ? (snapshot.diagnostic?.message ?? fallbackFailureMessage(snapshot.terminalOutcome))
          : null,
    };
  }

  if (!recording) {
    return { terminalOutcome: null, failureMessage: null };
  }

  return resolveDurableTerminalUiState(recording);
}

function resolveDurableTerminalUiState(recording: AudioRecordingObservable): TerminalUiState {
  if (recording.processingStatus === AudioRecordingProcessingStatus.COMPLETED) {
    return { terminalOutcome: null, failureMessage: null };
  }

  if (recording.uploadStatus === AudioRecordingUploadStatus.SKIPPED) {
    return {
      terminalOutcome: TerminalOutcome.NoUsableAudioFound,
      failureMessage: NO_USABLE_AUDIO_FOUND_MESSAGE,
    };
  }

  if (recording.uploadStatus === AudioRecordingUploadStatus.ERROR) {
    return durableUploadFailureUiState(recording);
  }

  if (recording.processingStatus === AudioRecordingProcessingStatus.ERROR) {
    const retranscriptionStatus = recording.modelData.retranscription_status;

    if (retranscriptionStatus === AudioRecordingRetranscriptionStatus.ERROR) {
      return {
        terminalOutcome: TerminalOutcome.RetranscribeFailed,
        failureMessage: "Transcription failed",
      };
    }

    return {
      terminalOutcome: TerminalOutcome.TransformFailed,
      failureMessage: "Processing failed",
    };
  }

  return { terminalOutcome: null, failureMessage: null };
}

function durableUploadFailureUiState(recording: AudioRecordingObservable): TerminalUiState {
  const latestUploadError = findLatestAppEvent(
    recording.appEvents,
    AudioRecordingAppEventKind.ERROR_UPLOADING
  );
  const persistedFailureKind = latestUploadError?.properties?.upload_failure_kind;
  const persistedErrorMessage =
    typeof latestUploadError?.properties?.error_message === "string" &&
    latestUploadError.properties.error_message.length > 0
      ? latestUploadError.properties.error_message
      : null;

  if (persistedFailureKind === "artifact-missing") {
    return {
      terminalOutcome: TerminalOutcome.AudioArtifactMissing,
      failureMessage: persistedErrorMessage ?? AUDIO_ARTIFACT_MISSING_MESSAGE,
    };
  }

  return {
    terminalOutcome: TerminalOutcome.UploadFailed,
    failureMessage: persistedErrorMessage ?? "Upload failed",
  };
}

function fallbackFailureMessage(outcome: TerminalOutcome): string {
  switch (outcome) {
    case TerminalOutcome.NoUsableAudioFound:
      return NO_USABLE_AUDIO_FOUND_MESSAGE;
    case TerminalOutcome.AudioArtifactMissing:
      return AUDIO_ARTIFACT_MISSING_MESSAGE;
    case TerminalOutcome.UploadFailed:
      return "Upload failed";
    case TerminalOutcome.RetranscribeFailed:
      return "Transcription failed";
    case TerminalOutcome.TransformFailed:
      return "Processing failed";
    case TerminalOutcome.UnexpectedFailed:
      return "Unexpected pipeline failure";
    case TerminalOutcome.Success:
      return "";
  }
}

/**
 * Whether the voice indicator chip should surface for a given note/recording.
 * The chip is shown while the recording is active, while the pipeline is
 * working, on any pipeline or recording failure, and whenever a voice
 * suggestion already exists on the note.
 */
export function shouldShowVoiceIndicator({
  recording,
  hasVoiceSuggestion,
  snapshot,
}: {
  recording: AudioRecordingObservable | null;
  hasVoiceSuggestion: boolean;
  snapshot: PipelineSnapshot | null;
}): boolean {
  if (hasVoiceSuggestion) return true;
  if (!recording) return false;

  if (recording.recordingStatus === AudioRecordingRecordingStatus.ERROR) return true;

  if (snapshot !== null && snapshot.stage !== Stage.Done) {
    return true;
  }

  const terminalUiState = resolveTerminalUiState({ snapshot, recording });
  if (
    terminalUiState.terminalOutcome !== null &&
    terminalUiState.terminalOutcome !== TerminalOutcome.Success
  ) {
    return true;
  }

  // Recording in progress before any snapshot exists (in-memory state lives
  // on the recording observable itself).
  if (recording.hasStartedRecording && recording.isRecording) return true;

  return false;
}

function deriveProcessingLabel({
  phase,
  snapshot,
}: {
  phase: AudioCapturePhase;
  snapshot: PipelineSnapshot | null;
}): string {
  if (phase === AudioCapturePhase.Uploading) {
    const percent =
      snapshot?.progress && typeof snapshot.progress.percent === "number"
        ? Math.min(100, Math.max(0, Math.round(snapshot.progress.percent)))
        : null;
    if (percent !== null) return `Uploading... (${percent}%)`;
    return snapshot?.stage === Stage.RetranscribeFromSource ? "Transcribing..." : "Uploading...";
  }
  return "Processing...";
}
